import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components/macro';

import {
  BreakpointUpSmall,
  BreakpointUpMedium,
  BreakpointUpLarge,
  BreakpointUpExtraLarge
} from '../../styles/Breakpoints';
import Portal from './Portal';

const Modal = ({
  title,
  children,
  close,
  backdrop,
  align,
  size 
}) => {
  return (
    <Portal>
      <ModalDialog align={align}>
        <ModalContent size={size}>
          <ModalHeader>
            {title && <div>{title}</div>}
            <button onClick={close}>
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path d="M12 10.303l-8.485-8.485-1.697 1.697 8.485 8.485-8.485 8.485 1.697 1.697 8.485-8.485 8.485 8.485 1.697-1.697-8.485-8.485 8.485-8.485-1.697-1.697-8.485 8.485z"></path>
              </svg>
            </button>
          </ModalHeader>

          <ModalBody>
            {children}
          </ModalBody>
        </ModalContent>
      </ModalDialog>

      {backdrop && <ModalBackdrop />}
    </Portal>
  );
};

export default Modal;

Modal.propTypes = {
  align: PropTypes.oneOf(['center','top']),
  size: PropTypes.oneOf(['sm','md','lg','xl']),
  backdrop: PropTypes.bool,
};

Modal.defaultProps = {
  align: 'center',
  size: 'sm',
  backdrop: true,
};

/* ---------------------- STYLES ---------------------- */

const ModalDialog = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1050;

  ${({ align }) => align === 'top' && css`
    padding-top: 40px;
    align-items: flex-start;
  `}
`;

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1040;
  width: 100vw;
  height: 100vh;
  background-color: #000;
  opacity: 0.5;
`;

const ModalContent = styled.div`
  width: 100%;
  background-color: #0d1117;                       /* Dark body */
  border-radius: 13px;
  border: 1px solid rgba(255,255,255,0.05);         /* Subtle outline */
  box-shadow: 0 8px 24px rgba(0,0,0,0.45);          /* Depth */

  ${BreakpointUpSmall} {
    width: 500px;
  }

  ${({ size }) => size === 'md' && css`
    ${BreakpointUpMedium} {
      width: 700px;
    }
  `}

  ${({ size }) => size === 'lg' && css`
    ${BreakpointUpLarge} {
      width: 900px;
    }
  `}

  ${({ size }) => size === 'xl' && css`
    ${BreakpointUpExtraLarge} {
      width: 1100px;
    }
  `}
`;

const ModalHeader = styled.div`
  padding: 14px 18px;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  background-color: #12171f;                        /* Dark header */
  color: #e6e8ec;
  font-size: 16px;
  font-weight: 600;

  border-top-left-radius: 13px;
  border-top-right-radius: 13px;
  border-bottom: 1px solid rgba(255,255,255,0.06);  /* Soft divider */

  button {
    display: inline-block;
    padding: 1px 4px;
    background-color: transparent;
    border-color: transparent;
    margin-left: auto;
    cursor: pointer;

    svg {
      fill: #707b8c;                                 /* Softer icon color */
      transition: 0.15s ease;
    }

    &:hover svg {
      fill: #ffffff;
    }

    &:focus {
      outline: none;
    }
  }
`;

const ModalBody = styled.div`
  padding: 18px 22px;
  background: #0d1117;
  color: #98a1b2;                                    /* Better readable grey */
  font-size: 0.95rem;
  line-height: 1.5;
`;
